import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Dumbbell, Heart, TrendingUp } from 'lucide-react';
import { BOX_IMAGES } from '@/data/images';

const EASE = [0.22, 1, 0.36, 1];

const pillars = [
  { icon: Heart, title: 'Le bien-être', sub: 'par le sport', num: '01' },
  { icon: TrendingUp, title: 'La santé', sub: 'par le mouvement', num: '02' },
  { icon: Dumbbell, title: 'La performance', sub: "par l'entraînement", num: '03' },
];

export const Promo = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);

  return (
    <section
      ref={ref}
      data-testid="promo-section"
      className="relative overflow-hidden bg-dark"
    >
      {/* Parallax bg */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img decoding="async"
          src={BOX_IMAGES.deadlift}
          alt="Entraînement CrossFit - soulevé de terre à CrossFit Saint-Chamond"
          className="w-full h-[120%] object-cover opacity-20"
          loading="lazy"
        />
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-br from-dark via-dark/95 to-primary-dark/40" />
      <div className="absolute inset-0 mesh-gradient opacity-50 pointer-events-none" />
      <div className="absolute inset-0 grain-overlay pointer-events-none" />

      <div className="container-custom relative z-10 py-24 md:py-32">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{ duration: 0.9, ease: EASE }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="text-primary-glow font-semibold text-xs tracking-[0.3em] uppercase">
            ━━ Notre philosophie ━━
          </span>
          <h2 className="font-heading text-5xl sm:text-6xl md:text-7xl text-white mt-4 leading-[0.88]">
            CROSSFIT & HYROX
            <br />
            <span className="text-gradient-primary italic font-display inline-block pr-2">
              TRAINING CLUB
            </span>
          </h2>
        </motion.div>

        {/* 3 Pillars */}
        <div className="grid md:grid-cols-3 gap-5 md:gap-6 mb-16">
          {pillars.map(({ icon: Icon, title, sub, num }, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{
                duration: 0.8,
                delay: 0.15 + idx * 0.15,
                ease: EASE,
              }}
              whileHover={{ y: -6 }}
              className="group relative glass-card rounded-3xl p-8 md:p-10 overflow-hidden transition-all duration-500 hover:border-primary-glow/50"
            >
              {/* Decorative number */}
              <span className="absolute top-4 right-5 font-display text-5xl text-white/5 group-hover:text-primary-glow/20 transition-colors duration-500">
                {num}
              </span>

              {/* Icon with glow */}
              <div className="relative w-16 h-16 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse-glow">
                <Icon className="h-7 w-7 text-primary-glow" strokeWidth={2} />
              </div>

              <h3 className="font-heading text-2xl md:text-3xl text-white mb-2 text-center tracking-wide">
                {title}
              </h3>
              <p className="text-primary-glow/80 text-sm font-medium text-center tracking-wider uppercase">
                {sub}
              </p>

              {/* Bottom accent */}
              <div className="mt-6 h-px w-full bg-gradient-to-r from-transparent via-primary-glow/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
          className="text-center"
        >
          <p className="text-white/60 text-xs tracking-[0.25em] uppercase mb-8">
            Coaching sportif &middot; Encadrement professionnel &middot; Accessible à tous
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="btn-shine w-full sm:w-auto bg-primary hover:bg-primary-light text-white font-heading tracking-wider px-10 py-7 rounded-xl glow-primary group"
              >
                REJOINS-NOUS
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link to="/tarifs">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/25 text-white hover:bg-white/10 hover:border-white/60 font-heading tracking-wider px-10 py-7 rounded-xl bg-transparent backdrop-blur-sm transition-all duration-300"
              >
                À PARTIR DE 59€/MOIS
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
